import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppHeader from "@/components/AppHeader";
import ConsentGate from "@/components/ConsentGate";
import VoiceRecorder from "@/components/VoiceRecorder";
import OctaveGrid from "@/components/OctaveGrid";
import ReportView from "@/components/ReportView";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { MailWarning, Lock, Loader2, RotateCcw } from "lucide-react";

const LOCKED = { emotional: "Emotional Frequency Mapping", blood: "Blood Biomarker Indicators" };

export default function Scan() {
  const { user, refreshUser } = useAuth();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(false);
  const [scanId, setScanId] = useState(null);
  const [grid, setGrid] = useState(null);
  const [physical, setPhysical] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(false);

  useEffect(() => {
    const existing = params.get("scan");
    if (!existing) return;
    setLoadingExisting(true);
    api.get(`/scans/${existing}`).then(async (r) => {
      setScanId(r.data.id);
      setGrid(r.data.grid);
      const phys = r.data.reports.find((x) => x.type === "physical");
      if (phys) {
        const full = await api.get(`/reports/${phys.id}`);
        setPhysical(full.data);
      }
    }).catch(() => toast.error("Could not load that scan."))
      .finally(() => setLoadingExisting(false));
  }, [params]);

  const handleAudio = async (blob, filename) => {
    setBusy(true);
    setGrid(null);
    setPhysical(null);
    const fd = new FormData();
    fd.append("file", blob, filename);
    try {
      const { data } = await api.post("/scan", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setScanId(data.scan_id);
      setGrid(data.grid);
      setPhysical(data.physical_report);
      toast.success("Scan complete — your Physical Health Mapping is ready.");
    } catch (err) {
      const msg = apiError(err.response?.data?.detail);
      toast.error(msg || "Recording unusable, please re-record.");
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setGrid(null); setPhysical(null); setScanId(null); };

  if (user && !user.email_verified) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="max-w-xl mx-auto px-5 py-20 text-center">
          <MailWarning className="text-primary mx-auto mb-5" size={40} strokeWidth={1.5} />
          <h1 className="font-serif text-3xl mb-3">Verify your email first</h1>
          <p className="text-muted-foreground mb-6">Scanning unlocks once your email is confirmed. Check your inbox for the verification link.</p>
          <Link to="/app"><Button className="rounded-full bg-primary text-primary-foreground font-semibold">Back to dashboard</Button></Link>
        </div>
      </div>
    );
  }

  if (user && !user.consent_accepted) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="px-5 py-16"><ConsentGate onAccepted={refreshUser} /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        {!grid && !loadingExisting && (
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-3">Health Scan</p>
            <h1 className="font-serif text-4xl tracking-tight mb-3">Record your voice</h1>
            <p className="text-muted-foreground mb-12 max-w-md mx-auto">Find a quiet space. Hold a steady vowel tone for up to 30 seconds. We analyse the frequencies across octaves 4–6.</p>
            <VoiceRecorder onComplete={handleAudio} busy={busy} />
          </div>
        )}

        {loadingExisting && <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>}

        {grid && (
          <div className="space-y-12">
            <div className="flex items-center justify-between">
              <h1 className="font-serif text-3xl tracking-tight">Your frequency map</h1>
              <Button variant="outline" className="rounded-full border-border" onClick={reset} data-testid="rescan-btn"><RotateCcw size={16} strokeWidth={1.5} /> New scan</Button>
            </div>
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8"><OctaveGrid grid={grid} /></div>

            <Tabs defaultValue="physical">
              <TabsList className="bg-secondary rounded-full p-1">
                <TabsTrigger value="physical" className="rounded-full" data-testid="tab-physical">Physical</TabsTrigger>
                <TabsTrigger value="emotional" className="rounded-full" data-testid="tab-emotional">Emotional</TabsTrigger>
                <TabsTrigger value="blood" className="rounded-full" data-testid="tab-blood">Blood</TabsTrigger>
              </TabsList>
              <TabsContent value="physical" className="pt-8">
                {physical ? <ReportView report={physical} /> : <div className="py-12 grid place-items-center"><Loader2 className="animate-spin text-primary" strokeWidth={1.5} /></div>}
              </TabsContent>
              <TabsContent value="emotional" className="pt-8"><LockedReport type="emotional" title={LOCKED.emotional} /></TabsContent>
              <TabsContent value="blood" className="pt-8"><LockedReport type="blood" title={LOCKED.blood} /></TabsContent>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

function LockedReport({ type, title }) {
  return (
    <div className="rounded-2xl border border-dashed border-border p-12 text-center" data-testid={`locked-${type}`}>
      <span className="grid place-items-center h-14 w-14 rounded-full bg-secondary text-primary mx-auto mb-5"><Lock strokeWidth={1.5} /></span>
      <h3 className="font-serif text-2xl mb-2">{title}</h3>
      <p className="text-muted-foreground text-sm max-w-md mx-auto mb-6">This report is part of a VoiceMatrix AI membership. Upgrade to unlock your {type} frequency mapping, sound-frequency prescriptions and the 11-day scan cycle.</p>
      <Link to="/products"><Button className="rounded-full bg-primary text-primary-foreground font-semibold" data-testid={`unlock-${type}-btn`}>See membership plans</Button></Link>
    </div>
  );
}
